import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/layout/Navbar';
import { useAuth } from '../context/AuthContext';
import { 
  Shield, 
  Users, 
  Package, 
  DollarSign, 
  BarChart3,
  Settings,
  ArrowLeft,
  ChevronRight,
  Clock,
  CheckCircle,
  CreditCard,
  Truck,
  AlertCircle,
  Loader2,
  RefreshCw,
  Search,
  Filter,
  AlertTriangle,
  XCircle,
  MoreVertical,
  Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status configuration
const STATUS_CONFIG = {
  'draft': { 
    label: 'Draft', 
    color: 'bg-gray-100 text-gray-600 border-gray-300',
    icon: Clock
  },
  'awaiting_shipment': { 
    label: 'Awaiting Shipment', 
    color: 'bg-blue-100 text-blue-800 border-blue-300',
    icon: Clock
  },
  'received': { 
    label: 'Received', 
    color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    icon: Package
  },
  'payment_processed': { 
    label: 'Payment Processed', 
    color: 'bg-orange-100 text-orange-800 border-orange-300',
    icon: CreditCard
  },
  'grading_complete': { 
    label: 'Grading Complete', 
    color: 'bg-purple-100 text-purple-800 border-purple-300',
    icon: CheckCircle
  },
  'id_and_encapsulation': { 
    label: 'Encapsulating', 
    color: 'bg-purple-100 text-purple-800 border-purple-300',
    icon: Package
  },
  'shipped': { 
    label: 'Shipped', 
    color: 'bg-green-100 text-green-800 border-green-300',
    icon: Truck
  },
  'complete': { 
    label: 'Complete', 
    color: 'bg-gray-100 text-gray-800 border-gray-300',
    icon: CheckCircle
  }
};

const getStatusConfig = (status) => {
  return STATUS_CONFIG[status] || { 
    label: status, 
    color: 'bg-gray-100 text-gray-800 border-gray-300',
    icon: Clock
  };
};

const AdminPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    users: 0,
    revenue: 0
  });
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [submissionToDelete, setSubmissionToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchSubmissions();
  }, [statusFilter]);

  const fetchSubmissions = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      // Don't send 'needs_attention' to backend - it's filtered client-side
      if (statusFilter && statusFilter !== 'needs_attention') {
        params.append('status_filter', statusFilter);
      }
      params.append('limit', '50');
      
      const [submissionsResponse, statsResponse] = await Promise.all([
        axios.get(`${API}/submissions/all?${params.toString()}`),
        axios.get(`${API}/admin/stats`)
      ]);
      
      setSubmissions(submissionsResponse.data);
      
      // Calculate stats
      const total = submissionsResponse.data.length;
      const pending = submissionsResponse.data.filter(s => 
        ['awaiting_shipment', 'received', 'payment_processed', 'grading_complete'].includes(s.status)
      ).length;
      const paymentFailed = submissionsResponse.data.filter(s => s.payment_failed === true).length;
      const revenue = submissionsResponse.data.reduce((sum, s) => sum + (s.total_price || 0), 0);
      
      setStats({
        total,
        pending,
        paymentFailed,
        users: statsResponse.data.total_users || 0,
        revenue
      });
    } catch (err) {
      console.error('Failed to fetch submissions:', err);
      setError('Failed to load submissions');
    } finally {
      setIsLoading(false);
    }
  };

  const openDeleteDialog = (submission, e) => {
    e.stopPropagation();
    setSubmissionToDelete(submission);
    setDeleteConfirmText('');
    setDeleteDialogOpen(true);
  };

  const handleDeleteSubmission = async () => {
    if (!submissionToDelete || deleteConfirmText !== 'DELETE') return;
    
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/admin/submissions/${submissionToDelete.id}`);
      setDeleteDialogOpen(false);
      setSubmissionToDelete(null);
      setDeleteConfirmText('');
      fetchSubmissions();
    } catch (err) {
      console.error('Failed to delete submission:', err);
      alert(err.response?.data?.detail || 'Failed to delete submission');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (amount == null) return '$0';
    return `$${Number(amount).toFixed(2)}`;
  };

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'needs_attention', label: '⚠️ Needs Attention' },
    { value: 'awaiting_shipment', label: 'Awaiting Shipment' },
    { value: 'received', label: 'Received' },
    { value: 'payment_processed', label: 'Payment Processed' },
    { value: 'grading_complete', label: 'Grading Complete' },
    { value: 'shipped', label: 'Shipped' },
    { value: 'complete', label: 'Complete' }
  ];

  // Filter submissions based on status filter
  const filteredSubmissions = statusFilter === 'needs_attention' 
    ? submissions.filter(s => s.payment_failed === true || s.requires_admin_attention === true)
    : submissions;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/dashboard" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Dashboard
          </Link>
          
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#003594] rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
              <p className="text-gray-500">Welcome, {user?.name}</p>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Submissions</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">In Progress</p>
                <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
              </div>
            </div>
          </div>
          {/* Payment Failed - Prominent Alert */}
          <div 
            className={`rounded-xl border p-4 cursor-pointer transition-all ${
              stats.paymentFailed > 0 
                ? 'bg-red-50 border-red-300 hover:bg-red-100' 
                : 'bg-white border-gray-200'
            }`}
            onClick={() => stats.paymentFailed > 0 && setStatusFilter('needs_attention')}
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                stats.paymentFailed > 0 ? 'bg-red-100' : 'bg-gray-100'
              }`}>
                <XCircle className={`w-5 h-5 ${stats.paymentFailed > 0 ? 'text-red-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <p className={`text-sm ${stats.paymentFailed > 0 ? 'text-red-700 font-medium' : 'text-gray-500'}`}>
                  Payment Failed
                </p>
                <p className={`text-2xl font-bold ${stats.paymentFailed > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                  {stats.paymentFailed || 0}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Users</p>
                <p className="text-2xl font-bold text-gray-900">{stats.users}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.revenue)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="mb-8">
          <Link 
            to="/admin/dealers"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
          >
            <Users className="w-4 h-4" />
            Manage Dealers
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Submissions Section */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Package className="w-5 h-5 text-[#003594]" />
                All Submissions
              </h2>
              <div className="flex items-center gap-3">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#003594]"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={fetchSubmissions}
                  disabled={isLoading}
                >
                  <RefreshCw className={`w-4 h-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </div>
          </div>

          {/* Submissions List */}
          <div className="divide-y divide-gray-100">
            {isLoading ? (
              <div className="p-12 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-[#003594] mx-auto mb-4" />
                <p className="text-gray-500">Loading submissions...</p>
              </div>
            ) : error ? (
              <div className="p-12 text-center">
                <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-4" />
                <p className="text-red-600">{error}</p>
                <Button onClick={fetchSubmissions} variant="outline" className="mt-4">
                  Try Again
                </Button>
              </div>
            ) : submissions.length === 0 ? (
              <div className="p-12 text-center">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No submissions found</p>
              </div>
            ) : (
              filteredSubmissions.map((submission) => {
                const statusConfig = getStatusConfig(submission.status);
                const StatusIcon = statusConfig.icon;
                const hasPaymentFailed = submission.payment_failed === true;
                
                return (
                  <div
                    key={submission.id}
                    onClick={() => navigate(`/admin/submissions/${submission.id}`)}
                    className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                      hasPaymentFailed ? 'bg-red-50/50 border-l-4 border-l-red-500' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="hidden sm:block">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            hasPaymentFailed ? 'bg-red-100' :
                            submission.status === 'awaiting_shipment' ? 'bg-blue-100' :
                            submission.status === 'received' ? 'bg-yellow-100' :
                            submission.status === 'payment_processed' ? 'bg-orange-100' :
                            submission.status === 'grading_complete' ? 'bg-purple-100' :
                            submission.status === 'shipped' ? 'bg-green-100' :
                            'bg-gray-100'
                          }`}>
                            {hasPaymentFailed ? (
                              <XCircle className="w-5 h-5 text-red-600" />
                            ) : (
                              <StatusIcon className={`w-5 h-5 ${
                                submission.status === 'awaiting_shipment' ? 'text-blue-600' :
                                submission.status === 'received' ? 'text-yellow-600' :
                                submission.status === 'payment_processed' ? 'text-orange-600' :
                                submission.status === 'grading_complete' ? 'text-purple-600' :
                                submission.status === 'shipped' ? 'text-green-600' :
                                'text-gray-600'
                              }`} />
                            )}
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-[#003594]">{submission.submission_number}</p>
                            {hasPaymentFailed && (
                              <div className="group relative">
                                <span 
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-red-600 text-white animate-pulse cursor-help"
                                >
                                  <AlertTriangle className="w-3 h-3" />
                                  PAYMENT FAILED
                                </span>
                                {/* Hover tooltip with details */}
                                <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-50">
                                  <div className="bg-gray-900 text-white text-xs rounded-lg p-3 min-w-[250px] max-w-[350px] shadow-lg">
                                    <p className="font-bold text-red-400 mb-1">Payment Failure Details</p>
                                    <p className="mb-1">
                                      <span className="text-gray-400">Reason:</span>{' '}
                                      {submission.payment_failure_reason || 'Unknown'}
                                    </p>
                                    {submission.stripe_decline_code && (
                                      <p className="mb-1">
                                        <span className="text-gray-400">Stripe Code:</span>{' '}
                                        <code className="bg-gray-800 px-1 rounded">{submission.stripe_decline_code}</code>
                                      </p>
                                    )}
                                    <p>
                                      <span className="text-gray-400">Attempts:</span>{' '}
                                      {submission.payment_attempt_count || 1} / 4
                                    </p>
                                    {submission.requires_admin_attention && (
                                      <p className="mt-2 text-yellow-400 font-medium text-[10px]">
                                        ⚠️ Requires admin attention
                                      </p>
                                    )}
                                    <div className="absolute bottom-0 left-4 transform translate-y-full">
                                      <div className="border-8 border-transparent border-t-gray-900"></div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-gray-500">
                            <span>{submission.card_count} cards</span>
                            <span>•</span>
                            <span>{formatCurrency(submission.total_price)}</span>
                            <span>•</span>
                            <span>{formatDate(submission.created_at)}</span>
                            {hasPaymentFailed && submission.stripe_decline_code && (
                              <>
                                <span>•</span>
                                <span className="text-red-600 font-medium">
                                  {submission.stripe_decline_code.replace(/_/g, ' ')}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`hidden sm:inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${statusConfig.color}`}>
                          {statusConfig.label}
                        </span>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`submission-actions-${submission.id}`}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem 
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/admin/submissions/${submission.id}`);
                              }}
                            >
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={(e) => openDeleteDialog(submission, e)}
                              className="text-red-600 focus:text-red-600"
                              data-testid={`delete-submission-${submission.id}`}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Submission
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Quick Links */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
          <Link to="/admin/vouchers" className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <span className="text-xl">🎟️</span>
              </div>
              <h3 className="font-semibold text-gray-900">Discount Codes</h3>
            </div>
            <p className="text-sm text-gray-500 mb-4">Create and manage discount code batches</p>
            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Active</span>
          </Link>
          <Link to="/admin/promos" className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <span className="text-xl">🏷️</span>
              </div>
              <h3 className="font-semibold text-gray-900">Promo Pricing</h3>
            </div>
            <p className="text-sm text-gray-500 mb-4">Special pricing for select collectors</p>
            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Active</span>
          </Link>
          <div className="bg-white rounded-xl border border-gray-200 p-6 opacity-50">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-yellow-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Pricing Rules</h3>
            </div>
            <p className="text-sm text-gray-500 mb-4">Configure pricing tiers and promotions</p>
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">Coming Soon</span>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-6 opacity-50">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Analytics</h3>
            </div>
            <p className="text-sm text-gray-500 mb-4">View business metrics and reports</p>
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">Coming Soon</span>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Delete Submission
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete submission{' '}
              <span className="font-bold text-gray-900">{submissionToDelete?.submission_number}</span>{' '}
              and all associated data including cards, status history, and payment records.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-800">
                <strong>Warning:</strong> You are about to delete:
              </p>
              <ul className="text-sm text-red-700 mt-2 list-disc list-inside">
                <li>{submissionToDelete?.card_count} cards</li>
                <li>All grading data and status history</li>
                <li>Payment and shipping records</li>
              </ul>
            </div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type <span className="font-mono font-bold text-red-600">DELETE</span> to confirm:
            </label>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="DELETE"
              className="font-mono"
              data-testid="delete-confirm-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteSubmission} 
              disabled={isDeleting || deleteConfirmText !== 'DELETE'}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              {isDeleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
